import React from "react";
import { Button } from "@/Components/ui";
import { Edit, Copy, Trash2, ImageIcon } from "lucide-react";

interface AmenityItem {
  id: number;
  name: string;
  image: string | null;
  description: string;
  created_at: string;
}

interface AmenityListProps {
  amenities: AmenityItem[];
  onEdit: (amenity: AmenityItem) => void;
  onCopy: (amenity: AmenityItem) => void;
  onDelete: (id: number) => void;
}

const AmenityList: React.FC<AmenityListProps> = ({
  amenities,
  onEdit,
  onCopy,
  onDelete,
}) => {
  if (amenities.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl p-12 text-center border-2 border-dashed border-gray-200">
        <ImageIcon size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          등록된 편의시설이 없습니다
        </h3>
        <p className="text-sm text-gray-500">
          상단의 "+ 편의시설 추가" 버튼을 클릭하여 새로운 편의시설을 등록하세요.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {amenities.map((amenity) => (
        <div
          key={amenity.id}
          className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all overflow-hidden"
        >
          <div className="p-4">
            <div className="flex items-start gap-3 mb-1">
              <div className="w-8 h-8 flex-shrink-0 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                {amenity.image ? (
                  <img
                    src={amenity.image}
                    alt={amenity.name}
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <ImageIcon size={24} className="text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-lg font-semibold text-gray-900 truncate mb-1">
                  {amenity.name}
                </h4>
                <p className="text-xs text-gray-500 line-clamp-2">
                  {amenity.description || "설명 없음"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 border-t border-gray-100">
              <Button
                color="soft-primary"
                size="sm"
                className="flex-1"
                onClick={() => onEdit(amenity)}
              >
                <Edit size={14} className="mr-1" />
              </Button>
              <Button
                color="soft-secondary"
                size="sm"
                className="flex-1"
                onClick={() => onCopy(amenity)}
              >
                <Copy size={14} className="mr-1" />
              </Button>
              <Button
                color="soft-danger"
                size="sm"
                onClick={() => onDelete(amenity.id)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AmenityList;
